import {Component, OnInit, Injector, ViewEncapsulation, inject, OnDestroy} from '@angular/core';

import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';

import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { WmsFeatureDirective } from "@wms/directives";

import { BaseComponent } from "../base/base.component";
import {PageWsService, WmsWorkspaceManagerService} from "@wms/workspace";
import {ApisService, DialogService} from '@wms/services';
import { WmsDialogComponent } from '@wms/components';
import { ImportProjectComponent, MoreActionsMenuComponent, ShortcutsDialogComponent } from '@wms/bottom-left-menu';

import { StudioFactory } from '@wms/app/services';
import {LogsDialogComponent} from "@wms/app/logs/components/logs-dialog/logs-dialog.component";
import {addResizableClass} from "@wms/core";
import {IssuesService} from "@wms/app/bottom-left-menu/components/issues/issues.service";

@Component({
  selector: 'wms-leftpanel',
  templateUrl: './leftpanel.component.html',
  styleUrls: ['./leftpanel.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    WmsFeatureDirective,
    BsDropdownModule,
    WmsDialogComponent,
    ShortcutsDialogComponent,
    ImportProjectComponent,
    MoreActionsMenuComponent,
    LogsDialogComponent
  ]
})
export class LeftpanelComponent extends BaseComponent implements OnInit, OnDestroy {
  public activeWSCategory: string = '';
  public studioFactory: StudioFactory
  public wsManager: WmsWorkspaceManagerService;
  private dialogService: DialogService;
  public pageWsService:PageWsService;
  public displayLogs: boolean;
  protected SETTINGS_TABS = ['Security', 'General', 'Profile Configuration', 'Artifacts', 'Prefab Configuration', 'Build Preferences'];
  protected STYLE_TABS = ['Global', 'Components'];
  public isPrism: boolean = false;


  private routeLinkVsCategoryMap: {[k:string]: string} = {
    'page' :'PAGE',
    'database':'DB',
    'java-service': 'JAVASERVICE',
    'apis':'APIDESIGNER',
    'settings':'SETTINGS',
    'mergechanges': 'MERGECHANGES',
    'files':'RESOURCE',
    'style':"STYLE",
    'ai-studio':"AISTUDIO"
  };

  showOpenIssueTabSubscription: any;
   private previousCategory:string = '';

  constructor(private router: Router, private apiService: ApisService, private issuesService: IssuesService) {
    super();
    this.wsManager = inject(WmsWorkspaceManagerService);
    this.studioFactory = inject(StudioFactory);
    this.activeWSCategory = this.wsManager.getActiveWSCategory();
    this.dialogService = inject(DialogService);
    this.pageWsService = inject(PageWsService);
    this.registerDestroyListener.add(this.eventManager.clearActivewsObservable().subscribe(() => {
      this.activeWSCategory = '';
    }));
    this.eventManager.setActiveWSCategoryObservable().subscribe((category: string) => {
      if (category === 'APISERVICE') {
        category = 'APIDESIGNER';
      }
      this.activeWSCategory = category;
    });
    this.showOpenIssueTabSubscription = this.eventManager.showOpenIssueTabObservable().subscribe((result: any) => {
      if(result.reimportedServiceId && result.reimportedServiceId === this.apiService.reimportedServiceId){
        this.dialogService.close("reImportLoader");
        if(result.goToAPIIssuesTab) {
          this.showIssuesTab('api-issues');
        }
      } else {
        this.showIssuesTab();
      }
    });
  }

  ngOnInit(): void {
    this.isPrism = this.projectService.isPrism();
    this.eventManager.openAPIDesignerDialogAsObservable().subscribe(data=> {
      if(data?.type === 'WEB_SERVICE') {
        this.switchWSCategory('APIDESIGNER', data.$event);
        this.apiService.openImportDialog();
      }
    });
  }

  showIssuesTab(activeTab?: string) {
    this.issuesService.activeTab = activeTab || 'inspect';
    this.issuesService.isEnabled = true;
    addResizableClass('wm-studio-content-issues')
  }

  getCurrentRouteInfo() {
    const urlSegments = this.router.url.split('/').filter(part => !!part);
    return {
      parentRoute: urlSegments[0],
      isSubpage: urlSegments.length > 1
    };
  }

  // Method to check if current route is a subpage
  get isSubpage(): boolean {
    return this.getCurrentRouteInfo().isSubpage;
  }

  // Get the route path based on category
  getRoutePath(category: string): string | null {
    const { parentRoute, isSubpage } = this.getCurrentRouteInfo();

    // Map category to route path
    const routePath = Object.entries(this.routeLinkVsCategoryMap)
      .find(([_, value]) => value === category)?.[0];

    // Only return null if we're on a subpage AND trying to navigate to the same category
    if (isSubpage && this.routeLinkVsCategoryMap[parentRoute] === category) {
      return null;
    }

    return routePath ? `/${routePath}` : null;
  }

  get showStyleWorkspace() {
    return this.isPrism || this.projectService.isDesignTokensEnabled;
  }

  switchWSCategory(category: string, event: MouseEvent, pageSubCategory?:string) {
    if(pageSubCategory){
      this.pageWsService.pageSubCategory = pageSubCategory;
      const name = (event.currentTarget as HTMLElement).getAttribute('name');
      this.eventManager.emitPageTabChange(name)
    }
    if(this.pageWsService.handelExpaindCollapse){
      this.pageWsService.toggleToolsPanelDisplay();
      this.pageWsService.handelExpaindCollapse = false;
    }
    if (this.previousCategory !== 'PAGE' ) {
      this.pageWsService.isActivetabDesign = 'DESIGN';
        this.pageWsService.selectedTab = 'MARKUP';
    }
    if(this.pageWsService.pageSubCategory === 'LIST'){
      this.pageWsService.pageSearchKey = '';
    }
    if(category === 'PAGE' && this.pageWsService.pageSubCategory === 'BUILD' && this.pageWsService.isActivetabDesign !== 'DESIGN' ){
      this.eventManager.emitOnTabswitch({tab:'DESIGN',details:{tab:'DESIGN'}});
    }
    this.previousCategory = category;

    const urlSegments = this.router.url.split('/').filter(part => !!part);
    const currentParentRoute = urlSegments[0];

    // If we're on a subpage and clicking the same category button, prevent navigation
    if (this.isSubpage && this.routeLinkVsCategoryMap[currentParentRoute] === category) {
      event.preventDefault();
      return;
    }

    // Now check for dirty workspace state
    if (this.wsManager?.isWSDirty()) {
      event.preventDefault();
      this.wsManager.saveActiveWs.next(() => {
        this.activeWSCategory = category;
      });
      return;
    }

    if (!this.activeWSCategory) {
      this.activeWSCategory = this.wsManager.getActiveWSCategory();
    }

    // For same category clicks on non-subpages, reload current URL
    if ((this.activeWSCategory === category ||
      category === this.routeLinkVsCategoryMap[currentParentRoute]) &&
      !this.isSubpage) {
      event.preventDefault();
      this.router.navigateByUrl(this.router.url);
      return;
    }

    this.activeWSCategory = category;
  }

  closeDialogWithId(id: any) {
    this.dialogService.close(id);
  }
  openLogsDialog(): void {
    this.dialogService.open('logsViewDialog')
  }
  handleHideLogs(): void {
    this.dialogService.close('logsViewDialog');
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if(this.showOpenIssueTabSubscription)
      this.showOpenIssueTabSubscription.unsubscribe();
  }
}

